<script>
export default {
    name: 'ProjectionWeeklyPlan',
    props: {
        group: { type: Object, default: null },
        period: { type: String, default: null },
        originalWeeks: { type: Array, default: () => [] },
        originalTarget: { type: Number, default: null },
        originalInitial: { type: Number, default: null },
        originalWarnings: { type: Array, default: () => [] }
    },
    data() { return { now: Date.now(), clock: null }; },
    computed: {
        rows() {
            const weekly = this.group && Array.isArray(this.group.rows) ? this.group.rows : [];
            // The original calendar/weights may have been rebuilt after a weekly freeze.
            // Never label a saved weekly weight as the current original weight.
            const calendar = this.originalWeeks.length ? this.originalWeeks : weekly;
            return calendar.map(week => {
                const original = this.originalWeeks.find(row => row.start === week.start && row.end === week.end);
                const row = weekly.find(row => row.start === week.start && row.end === week.end);
                return { start: week.start, end: week.end, state: 'pending', coverage: 'unknown', ...row,
                    original_target: original ? original.target : row && row.original_target,
                    original_weight: original ? original.weight : null };
            });
        },
        totals() {
            return { weight: this.sum(this.rows.map(row => row.original_weight)),
                target: this.sum(this.rows.map(row => row.original_target)) };
        },
        today() {
            const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(this.now));
            const part = type => parts.find(item => item.type === type).value;
            return `${part('year')}-${part('month')}-${part('day')}`;
        }
    },
    mounted() { this.clock = setInterval(() => { this.now = Date.now(); }, 15000); },
    beforeDestroy() { clearInterval(this.clock); },
    methods: {
        sum(values) { return values.length && values.every(value => Number.isFinite(value) && value >= 0) ? values.reduce((sum, value) => sum + value, 0) : null; },
        percent(value) { return Number.isFinite(value) ? (value * 100).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' %' : 'Sin dato'; },
        number(value) { return value == null ? 'Sin dato' : Number(value).toLocaleString('es-CL'); },
        date(value) { return value ? value.split('-').reverse().join('/') : 'Sin dato'; },
        current(row) { return row.start <= this.today && this.today <= row.end; },
        state(value) { return { saved: 'Fijada', simulated: 'Histórico', forecast: 'Proyección', unrecorded: 'Sin registro', pending: 'Pendiente' }[value] || 'Sin dato'; },
        actual(row) {
            if (row.coverage === 'unknown' || row.actual == null) return 'Sin dato';
            return this.number(row.actual);
        },
        warning(value) {
            const messages = {
                missing_or_invalid_original: 'Falta un plan original válido.',
                missing_or_invalid_staff: 'Falta una dotación válida.',
                missing_or_invalid_target: 'Falta una meta válida.',
                missing_or_invalid_dates: 'Faltan fechas de ventas válidas.',
                missing_coverage: 'Corte de cobertura no informado.',
                invalid_coverage: 'Corte de cobertura inválido.',
                insufficient_coverage: 'La cobertura no alcanza el día anterior a esta semana.',
                period_not_active: 'Período fuera de su calendario activo.',
                invalid_context: 'Calendario semanal no válido.',
                unallocatable_weights_or_total: 'No se puede distribuir el saldo con los pesos disponibles.',
                frozen_staff_changed: 'Cambió la dotación; se conserva la meta semanal fijada.',
                frozen_target_changed: 'Cambió la meta comercial; se conserva la meta semanal fijada.',
                frozen_original_hash_changed: 'Cambió el plan original; se conserva el reparto semanal guardado.',
                frozen_snapshot_hash_changed: 'Cambió el Excel; se actualiza el avance, no la meta semanal fijada.',
                saved_original_version_changed: 'Hay semanas guardadas con otra versión del plan original.',
                retrospective_simulation: 'Histórico reconstruido con los archivos de ventas, meta y dotación actuales; no corresponde a metas guardadas en esas fechas.'
            };
            return messages[value] || `Aviso del plan: ${value}`;
        },
        originalWarning(value) {
            const messages = {
                frozen_target_changed: 'La meta actual difiere de la meta original congelada; el plan original se conserva.',
                frozen_calendar_changed: 'El calendario vigente difiere del congelado; se conservan las fechas originales.',
                fallback_calendar_days: 'Sin históricos utilizables: pesos originales por días de calendario.',
                initial_unconfirmed: 'Saldo inicial no confirmado; cero se usa solo para planificar, no como venta observada.',
                initial_exceeds_target: 'Las matrículas anticipadas superaban la meta al congelar el plan.'
            };
            return messages[value] || `Aviso de validez del plan original: ${value}`;
        }
    }
};
</script>

<template>
    <section class="weekly-plan" aria-label="Historial y proyección semanal">
        <header><h2>Plan semanal · original y recalculado</h2><span>{{ period }}</span></header>
        <p>La meta semanal fijada no cambia con las cargas diarias. «Histórico» identifica semanas reconstruidas con las ventas, meta y dotación actuales; las futuras son proyecciones.</p>
        <p v-if="originalTarget != null">Meta original del período: <strong>{{ number(originalTarget) }}</strong>. Cuotas semanales: {{ number(totals.target) }} · saldo inicial: {{ number(originalInitial) }}. El saldo inicial no se suma otra vez a las cuotas.</p>
        <p v-if="!group" role="status">Sin plan semanal disponible para este período y segmento; se muestra solo el original si existe.</p>
        <p v-if="!rows.length" role="status">Sin plan semanal disponible para este período y segmento.</p>
        <div v-else class="weekly-scroll" role="region" aria-label="Tabla semanal" tabindex="0">
            <table>
                <thead><tr><th scope="col">Semana · inicio / fin</th><th scope="col">Peso original %</th><th scope="col">Meta original</th><th scope="col">Meta recalculada</th><th scope="col">Matrículas reales</th><th scope="col">Estado</th></tr></thead>
                <tbody><tr v-for="row in rows" :key="row.start" :class="{ 'current-week': current(row) }" :aria-current="current(row) ? 'date' : null">
                    <th scope="row">{{ date(row.start) }} — {{ date(row.end) }} <span v-if="current(row)" class="today">Semana actual</span></th>
                    <td>{{ percent(row.original_weight) }}</td><td>{{ number(row.original_target) }}</td><td>{{ number(row.updated_target) }}</td><td>{{ actual(row) }}</td><td><span class="state" :class="row.state">{{ state(row.state) }}</span></td>
                </tr></tbody>
                <tfoot><tr><th scope="row">Total metas semanales originales</th><td>{{ percent(totals.weight) }}</td><td>{{ number(totals.target) }}</td><td colspan="3">No se suman metas históricas, fijadas y proyectadas como un objetivo único.</td></tr></tfoot>
            </table>
        </div>
    </section>
</template>

<style scoped>
.weekly-plan { margin: 24px 0; padding: 22px; background: #fff; border: 1px solid #e0e6ef; border-radius: 14px; color: #24324a; }
header { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 10px; align-items: center; margin-bottom: 12px; }
h2 { font-size: 18px; margin: 0; } header span { color: #65738b; font-size: 12px; }
p, .weekly-warnings { font-size: 12px; line-height: 1.6; color: #65738b; }
.weekly-warnings { padding: 12px 12px 12px 28px; background: #fff8eb; border-radius: 8px; color: #855912; }
.weekly-scroll { overflow-x: auto; max-height: 480px; }
table { width: 100%; border-collapse: collapse; font-size: 12px; }
th, td { text-align: left; padding: 10px 12px; border-bottom: 1px solid #e8edf4; }
thead th { position: sticky; top: 0; background: #f4f6fb; z-index: 1; white-space: nowrap; }
tbody th { font-weight: 500; min-width: 210px; }
tfoot { background: #f4f6fb; font-weight: 600; }
.current-week { background: #edf8f5; } .current-week th { box-shadow: inset 3px 0 #0d9488; }
.today, .state { display: inline-block; padding: 3px 7px; border-radius: 5px; white-space: nowrap; font-size: 11px; }
.today, .saved { color: #086d62; background: #d9f1e9; } .forecast { color: #5345aa; background: #f0edfc; }
.unrecorded, .pending { color: #65738b; background: #edf0f5; }
.simulated { color: #855912; background: #fff1d6; }
@media (max-width: 600px) { .weekly-plan { padding: 14px; } }
</style>